/**
 * Cron utilities for parsing and converting cron expressions to natural language
 */

interface CronParts {
  minute: string
  hour: string
  dayOfMonth: string
  month: string
  dayOfWeek: string
}

/**
 * Parse a cron expression into its parts
 */
export function parseCronExpression(cron: string): CronParts | null {
  const parts = cron.trim().split(/\s+/)

  if (parts.length !== 5) {
    return null
  }

  // Ensure all parts exist before returning
  if (!parts[0] || !parts[1] || !parts[2] || !parts[3] || !parts[4]) {
    return null
  }

  return {
    minute: parts[0],
    hour: parts[1],
    dayOfMonth: parts[2],
    month: parts[3],
    dayOfWeek: parts[4],
  }
}

/**
 * Convert cron expression to human-readable description
 */
export function cronToNaturalLanguage(cron: string): string {
  const parts = parseCronExpression(cron)

  if (!parts) {
    return "Invalid cron expression"
  }

  const { minute, hour, dayOfMonth, month, dayOfWeek } = parts

  // Common patterns
  if (cron === "* * * * *") {
    return "Every minute"
  }

  if (cron === "0 * * * *") {
    return "Every hour"
  }

  if (cron === "0 0 * * *") {
    return "Daily at midnight"
  }

  if (cron === "0 0 * * 0") {
    return "Weekly on Sunday at midnight"
  }

  if (cron === "0 0 1 * *") {
    return "Monthly on the 1st at midnight"
  }

  if (cron === "0 0 1 1 *") {
    return "Yearly on January 1st at midnight"
  }

  // Build description parts
  const timePart = buildTimePart(minute, hour)
  const datePart = buildDatePart(dayOfMonth, month, dayOfWeek)

  return `${timePart}${datePart}`.trim()
}

function buildTimePart(minute: string, hour: string): string {
  if (minute === "*" && hour === "*") {
    return "Every minute"
  }

  if (minute === "0" && hour === "*") {
    return "Every hour"
  }

  if (minute !== "*" && hour === "*") {
    if (minute.includes("/")) {
      const interval = minute.split("/")[1]
      return `Every ${interval} minutes`
    }
    if (minute.includes(",")) {
      const minutes = minute.split(",").join(", ")
      return `At minutes ${minutes} of every hour`
    }
    return `At minute ${minute} of every hour`
  }

  if (minute === "*" && hour !== "*") {
    if (hour.includes("/")) {
      const interval = hour.split("/")[1]
      return `Every ${interval} hours`
    }
    if (hour.includes(",")) {
      const hours = hour
        .split(",")
        .map((h) => formatHour(h))
        .join(", ")
      return `Every minute during hours ${hours}`
    }
    return `Every minute during hour ${formatHour(hour)}`
  }

  // Specific time
  if (!minute.includes("*") && !hour.includes("*")) {
    return `At ${formatTime(hour, minute)}`
  }

  return "At custom time"
}

function buildDatePart(dayOfMonth: string, month: string, dayOfWeek: string): string {
  if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    return " every day"
  }

  if (dayOfWeek !== "*") {
    const days = dayOfWeek
      .split(",")
      .map((d) => formatDayOfWeek(d))
      .join(", ")
    if (dayOfMonth === "*" && month === "*") {
      return ` on ${days}`
    }
  }

  if (dayOfMonth !== "*") {
    if (dayOfMonth.includes("/")) {
      const interval = dayOfMonth.split("/")[1]
      return ` every ${interval} days`
    }
    if (month === "*") {
      return ` on day ${dayOfMonth} of every month`
    }
    if (month !== "*") {
      const monthName = formatMonth(month)
      return ` on ${monthName} ${dayOfMonth}`
    }
  }

  if (month !== "*") {
    const monthName = formatMonth(month)
    return ` in ${monthName}`
  }

  return ""
}

function formatTime(hour: string, minute: string): string {
  const h = Number.parseInt(hour)
  const m = Number.parseInt(minute)
  const period = h >= 12 ? "PM" : "AM"
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${displayHour}:${m.toString().padStart(2, "0")} ${period}`
}

function formatHour(hour: string): string {
  const h = Number.parseInt(hour)
  const period = h >= 12 ? "PM" : "AM"
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${displayHour} ${period}`
}

function formatDayOfWeek(day: string): string {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
  const dayNum = Number.parseInt(day)
  return days[dayNum] || day
}

function formatMonth(month: string): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]
  const monthNum = Number.parseInt(month) - 1
  return months[monthNum] || month
}

/**
 * Validate cron expression
 */
export function validateCronExpression(cron: string): { valid: boolean; error?: string } {
  const parts = parseCronExpression(cron)

  if (!parts) {
    return { valid: false, error: "Cron expression must have exactly 5 parts separated by spaces" }
  }

  const { minute, hour, dayOfMonth, month, dayOfWeek } = parts

  // Validate ranges
  if (!isValidCronPart(minute, 0, 59)) {
    return { valid: false, error: "Minute must be 0-59 or valid cron expression" }
  }

  if (!isValidCronPart(hour, 0, 23)) {
    return { valid: false, error: "Hour must be 0-23 or valid cron expression" }
  }

  if (!isValidCronPart(dayOfMonth, 1, 31)) {
    return { valid: false, error: "Day of month must be 1-31 or valid cron expression" }
  }

  if (!isValidCronPart(month, 1, 12)) {
    return { valid: false, error: "Month must be 1-12 or valid cron expression" }
  }

  if (!isValidCronPart(dayOfWeek, 0, 7)) {
    return { valid: false, error: "Day of week must be 0-7 (0 and 7 are Sunday) or valid cron expression" }
  }

  return { valid: true }
}

function isValidCronPart(part: string, min: number, max: number): boolean {
  // Allow wildcards
  if (part === "*") return true

  // Allow ranges (e.g., 1-5)
  if (part.includes("-")) {
    const rangeParts = part.split("-").map(Number)
    const start = rangeParts[0]
    const end = rangeParts[1]

    if (start === undefined || end === undefined) return false
    return start >= min && end <= max && start <= end
  }

  // Allow steps (e.g., */5)
  if (part.includes("/")) {
    const stepParts = part.split("/")
    const range = stepParts[0]
    const step = stepParts[1]

    if (!range || !step) return false

    if (range === "*") return Number(step) > 0
    return isValidCronPart(range, min, max) && Number(step) > 0
  }

  // Allow lists (e.g., 1,3,5)
  if (part.includes(",")) {
    return part.split(",").every((p) => isValidCronPart(p.trim(), min, max))
  }

  // Single number
  const num = Number(part)
  return !Number.isNaN(num) && num >= min && num <= max
}

/**
 * Get common cron presets
 */
export function getCronPresets() {
  return [
    { name: "Every minute", cron: "* * * * *" },
    { name: "Every 5 minutes", cron: "*/5 * * * *" },
    { name: "Every 15 minutes", cron: "*/15 * * * *" },
    { name: "Every 30 minutes", cron: "*/30 * * * *" },
    { name: "Every hour", cron: "0 * * * *" },
    { name: "Every 2 hours", cron: "0 */2 * * *" },
    { name: "Every 6 hours", cron: "0 */6 * * *" },
    { name: "Daily at midnight", cron: "0 0 * * *" },
    { name: "Daily at 9 AM", cron: "0 9 * * *" },
    { name: "Daily at 5 PM", cron: "0 17 * * *" },
    { name: "Weekly (Sunday midnight)", cron: "0 0 * * 0" },
    { name: "Weekly (Monday 9 AM)", cron: "0 9 * * 1" },
    { name: "Monthly (1st at midnight)", cron: "0 0 1 * *" },
    { name: "Yearly (Jan 1st at midnight)", cron: "0 0 1 1 *" },
    { name: "Weekdays at 9 AM", cron: "0 9 * * 1-5" },
    { name: "Weekends at 10 AM", cron: "0 10 * * 6,0" },
  ]
}
